import {
  Autocomplete,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { Permissions, useScopeChecker } from 'src/rbac';
import { DateTimePicker, renderTimeViewClock } from '@mui/x-date-pickers';
import { useFormik } from 'formik';
import moment from 'moment';
import { CSSProperties, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clockInActivityType } from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import { LaborLocationTypeIcons } from 'src/pages/therapist-dashboard/dashboard-icon';
import { getAllTherapistAction } from 'src/redux/actions/documentationAction';
import {
  addLaborHoursAction,
  editLaborHoursAction,
  getLocationTypeLaborHr,
} from 'src/redux/actions/laborHourActions';
import {
  convertDateToSelectedTimeZone,
  convertIntoIsoFormat,
  formatDateWithTimezone,
  formatDateWithTimezoneWithZeroSecond,
} from 'src/utils/convertDateToSelectedTimeZone';
import * as Yup from 'yup';

type Props = {
  close: any;
  openHoursType: any;
  selectedDate: any;
};

const APPLY_TRNG_LABOR_CODE = 'APPLY_TRNG_LABOR_CODE';
/**
 * APIT-699: SICK and PTOPO (PTO Payout). Raw scopeKeys like TRNG's, because the installed
 * @restore/permission-catalog does not carry them yet; use-permission.tsx bridges their display
 * names. The API lists every labor code unfiltered and never checks these on save, so the filter
 * below is the only gate — a code without a branch there stays hidden, whoever the user is.
 */
const APPLY_SICK_LABOR_CODE = 'APPLY_SICK_LABOR_CODE';
const APPLY_PTOPO_LABOR_CODE = 'APPLY_PTOPO_LABOR_CODE';

/** APIT-710: PTO advance-dating allowance, in days, and the message shown when it's exceeded. */
const PTO_ADVANCE_DAYS = 90;
const PTO_TOO_FAR_MESSAGE = `PTO cannot be dated more than ${PTO_ADVANCE_DAYS} days in the future`;

/**
 * APIT-710: a "PTO entry" is Location Building with Labor Code PTO. Stale-state guard — if the
 * user switches Location or Labor Code away from this after picking a future date, this flips
 * back to false and the ordinary (today) limit re-applies on save.
 */
const isPtoEntry = (type: any, laborCode: any) => type === 'BUILDING' && laborCode?.key === 'PTO';

/**
 * APIT-710: the latest date/time a clock in or out may be saved. PTO gets a 90-day advance
 * allowance (end of the facility-local day, so a typed time on day 90 is still allowed);
 * everything else keeps today's facility-local "now".
 */
const getFutureDateLimit = (type: any, laborCode: any) => {
  const facilityNow = new Date(convertDateToSelectedTimeZone(new Date()));
  return isPtoEntry(type, laborCode)
    ? moment(facilityNow).add(PTO_ADVANCE_DAYS, 'days').endOf('day').toDate()
    : facilityNow;
};

const EditLaborManagement = (props: Props) => {
  const { close, openHoursType, selectedDate } = props;
  const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
  const [checkActivityType] = useFeatureToggle(clockInActivityType, 'ENTERPRISE');
  const has = useScopeChecker();
  const [providerId] = useCurrentUser(['providerId']);
  const dispatch = useDispatch();
  const modalStyle: CSSProperties = {
    position: 'fixed',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    WebkitTransform: 'translate(-50%, -50%)',
  };
  const modalContainerStyle: CSSProperties = {
    backgroundColor: '#fff',
    width: '600px',
    height: 'auto',
    paddingBottom: '24px',
  };

  const allTherapist = useSelector((state: any) => state.getAllTherapistReducer);
  const locationType = useSelector((state: any) => state.getLocationTypeLaborHrReducer);
  const addLaborHrsResponse = useSelector((state: any) => state.addLaborHoursReducer);
  const editLaborHrsResponse = useSelector((state: any) => state.editLaborHoursReducer);
  const getLaborCodesResponse = useSelector((state: any) => state.getLaborCodesReducer?.response);
  const getLaborCodesResponseWithPermission = getLaborCodesResponse?.filter((code: any) => {
    if (code?.key === 'REHAB_TRAV') {
      if (has(Permissions.APPLY_REHAB_TRAV_LABOR_CODE)) return true;
      else return false;
    } else if (code?.key === 'FLOAT') {
      if (has(Permissions.APPLY_FLOAT_LABOR_CODE)) return true;
      else return false;
    } else if (code?.key === 'PRNEDU') {
      if (has(Permissions.APPLY_PRNEDU_LABOR_CODE)) return true;
      else return false;
    } else if (code?.key === 'BEREV') {
      if (has(Permissions.APPLY_BEREV_LABOR_CODE)) return true;
      else return false;
    } else if (code?.key === 'HOL') {
      if (has(Permissions.APPLY_HOL_LABOR_CODE)) return true;
      else return false;
    } else if (code?.key === 'PTO') {
      if (has(Permissions.APPLY_PTO_LABOR_CODE)) return true;
      else return false;
    } else if (code?.key === 'JURY') {
      if (has(Permissions.APPLY_JURY_LABOR_CODE)) return true;
      else return false;
    } else if (code?.key === 'TRNG') {
      if (has(APPLY_TRNG_LABOR_CODE)) return true;
      else return false;
    } else if (code?.key === 'SICK') {
      if (has(APPLY_SICK_LABOR_CODE)) return true;
      else return false;
    } else if (code?.key === 'PTOPO') {
      if (has(APPLY_PTOPO_LABOR_CODE)) return true;
      else return false;
    }
    return false;
  });

  const isLaborCodeDropdownShow =
    has(APPLY_TRNG_LABOR_CODE) ||
    has(APPLY_SICK_LABOR_CODE) ||
    has(APPLY_PTOPO_LABOR_CODE) ||
    has(Permissions.APPLY_JURY_LABOR_CODE) ||
    has(Permissions.APPLY_PTO_LABOR_CODE) ||
    has(Permissions.APPLY_HOL_LABOR_CODE) ||
    has(Permissions.APPLY_BEREV_LABOR_CODE) ||
    has(Permissions.APPLY_PRNEDU_LABOR_CODE) ||
    has(Permissions.APPLY_REHAB_TRAV_LABOR_CODE) ||
    has(Permissions.APPLY_FLOAT_LABOR_CODE);
  useEffect(() => {
    dispatch(getAllTherapistAction(null, null, null));
    dispatch(getLocationTypeLaborHr());
  }, [dispatch]);

  interface LaborHours {
    type: string;
    therapist?: any;
    mileage?: any;
    clockIn?: Date;
    clockOut?: Date;
  }

  function extractTimeFromDate(date: any) {
    const d = new Date(date);
    return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
  }

  const laborHoursValidation: Yup.ObjectSchema<LaborHours> = Yup.object().shape({
    type: Yup.string().required('Please select a location'),
    therapist: Yup.string().required('Please select a therapist'),
    laborCode: Yup.object()
      .shape({
        displayName: Yup.string().nullable(),
        key: Yup.string().nullable(),
      })
      .nullable(),
    clockInActivityType: Yup.string().nullable(),
    mileage: Yup.number().test(
      'custom-validation',
      'Please Enter valid Mileage',
      // @ts-ignore
      function (value: any) {
        const type = this.parent.type;
        if (type === 'MILEAGE') {
          return Yup.number().min(0).validateSync(value);
        }
        return true;
      }
    ),
    clockIn: Yup.date()
      .test(
        'custom-validation',
        'Please Enter valid time',
        // @ts-ignore
        function (value: any) {
          const type = this.parent.type;
          if (type !== 'MILEAGE') {
            return Yup.date().min(0).validateSync(value);
          }
          return true;
        }
      )
      .test(
        'clock-out-after-clock-in',
        'Clock Out time must not be earlier or equal than clock In time',
        function (value) {
          const type = this.parent.type;
          if (type !== 'MILEAGE') {
            const clockOutValue = this.parent.clockOut;
            if (value && clockOutValue) {
              const clockInTime = extractTimeFromDate(value);
              const clockOutTime = extractTimeFromDate(clockOutValue);
              return clockInTime <= clockOutTime;
            }
          }
          return true;
        }
      )
      .test('no-future-date-or-time', 'Cannot select a future date or time', function (value) {
        const type = this.parent.type;
        if (type !== 'MILEAGE') {
          if (!value) return false;
          const selectedDate =
            openHoursType?.type === 'EDIT'
              ? new Date(convertDateToSelectedTimeZone(value))
              : new Date(value);
          const limit = getFutureDateLimit(type, this.parent.laborCode);
          if (selectedDate <= limit) return true;
          return isPtoEntry(type, this.parent.laborCode)
            ? this.createError({ message: PTO_TOO_FAR_MESSAGE })
            : false;
        }
        return true;
      }),
    clockOut: Yup.date()
      .test(
        'custom-validation',
        'Please Enter valid time',
        // @ts-ignore
        function (value: any) {
          const type = this.parent.type;
          if (type !== 'MILEAGE') {
            return Yup.date().min(0).validateSync(value);
          }
          return true;
        }
      )
      .test(
        'clock-out-after-clock-in',
        'Clock Out time must not be earlier or equal than clock In time',
        function (value) {
          const type = this.parent.type;
          if (type !== 'MILEAGE') {
            const clockInValue = this.parent.clockIn;
            if (value && clockInValue) {
              const clockInTime = extractTimeFromDate(clockInValue);
              const clockOutTime = extractTimeFromDate(value);
              return clockOutTime > clockInTime;
            }
          }
          return true;
        }
      )
      .test('no-future-date-or-time', 'Cannot select a future date or time', function (value) {
        const type = this.parent.type;
        if (type !== 'MILEAGE') {
          if (!value) return false;
          const selectedDate = new Date(value);
          const limit = getFutureDateLimit(type, this.parent.laborCode);
          if (selectedDate <= limit) return true;
          return isPtoEntry(type, this.parent.laborCode)
            ? this.createError({ message: PTO_TOO_FAR_MESSAGE })
            : false;
        }
        return true;
      }),
  });

  const formik = useFormik({
    initialValues: {
      type: openHoursType?.type === 'EDIT' ? openHoursType?.value?.locationType : '',
      laborCode:
        openHoursType?.type === 'EDIT'
          ? {
              key: openHoursType?.value?.laborCode,
              displayName:
                getLaborCodesResponse?.find(
                  (code: any) => code?.key === openHoursType?.value?.laborCode
                )?.displayName || '',
            }
          : {
              displayName: '',
              key: '',
            },
      therapist: openHoursType?.type === 'EDIT' ? openHoursType?.value?.therapistId : '',
      mileage:
        openHoursType?.type === 'EDIT' && !!openHoursType?.value?.mileage
          ? openHoursType?.value?.mileage
          : '',
      clockIn:
        openHoursType?.type === 'EDIT' && !!openHoursType?.value?.clockIn
          ? new Date(convertDateToSelectedTimeZone(openHoursType?.value?.clockIn))
          : selectedDate,
      clockOut:
        openHoursType?.type === 'EDIT' && !!openHoursType?.value?.clockIn
          ? new Date(convertDateToSelectedTimeZone(openHoursType?.value?.clockOut))
          : selectedDate,
      clockInActivityType:
        openHoursType?.type === 'EDIT' ? openHoursType?.value?.clockInActivityType : '',
    },
    validationSchema: laborHoursValidation,
    onSubmit: (values) => {
      if (openHoursType?.type === 'EDIT') {
        if (values?.type === 'MILEAGE') {
          dispatch(
            editLaborHoursAction({
              locationType: values?.type,
              therapistId: values?.therapist,
              laborCode: null,
              mileage: values?.mileage,
              labourManagementId: openHoursType?.value?.labourManagementId,
              selectDate: convertIntoIsoFormat(formatDateWithTimezone(selectedDate)),
              clockInActivityType: null,
              param: {
                zoneId: getFacilityTimeZone,
              },
            })
          );
        } else {
          dispatch(
            editLaborHoursAction({
              locationType: values?.type,
              therapistId: values?.therapist,
              laborCode: values?.type === 'BUILDING' ? values?.laborCode?.key : null,
              labourManagementId: openHoursType?.value?.labourManagementId,
              clockIn: convertIntoIsoFormat(formatDateWithTimezoneWithZeroSecond(values?.clockIn)),
              clockOut: convertIntoIsoFormat(
                formatDateWithTimezoneWithZeroSecond(values?.clockOut)
              ),
              clockInActivityType:
                values?.clockInActivityType && values?.type === 'BUILDING'
                  ? values?.clockInActivityType
                  : null,
              selectDate: convertIntoIsoFormat(formatDateWithTimezone(selectedDate)),
              param: {
                zoneId: getFacilityTimeZone,
              },
            })
          );
        }
        return;
      } else {
        if (values?.type === 'MILEAGE') {
          dispatch(
            addLaborHoursAction({
              locationType: values?.type,
              laborCode: values?.type === 'BUILDING' ? values?.laborCode?.key : null,
              therapistId: values?.therapist,
              mileage: values?.mileage,
              selectDate: convertIntoIsoFormat(formatDateWithTimezone(selectedDate)),
              clockInActivityType: null,
              param: {
                zoneId: getFacilityTimeZone,
              },
            })
          );
        } else {
          dispatch(
            addLaborHoursAction({
              locationType: values?.type,
              laborCode: values?.type === 'BUILDING' ? values?.laborCode?.key : null,
              therapistId: values?.therapist,
              clockIn: convertIntoIsoFormat(formatDateWithTimezoneWithZeroSecond(values?.clockIn)),
              clockOut: convertIntoIsoFormat(
                formatDateWithTimezoneWithZeroSecond(values?.clockOut)
              ),
              clockInActivityType:
                values?.clockInActivityType && values?.type === 'BUILDING'
                  ? values?.clockInActivityType
                  : null,
              selectDate: convertIntoIsoFormat(formatDateWithTimezone(selectedDate)),
              param: {
                zoneId: getFacilityTimeZone,
              },
            })
          );
        }
      }
    },
  });

  /**
   * THIN-8096: the Therapist Name picker offered every provider in the facility regardless of
   * permission, so a user holding only Edit Own Labor could select a teammate, fill the dialog in
   * and submit an entry the API then refused.
   *
   * Edit Team Labor is the only permission that lets you write another provider's hours —
   * `LaborEditAccessPolicy.canEditEntry` is `hasEditTeamAccess() || (hasEditOwnAccess() &&
   * isOwner(target))`, and it does not accept the legacy Create/Update/Delete Labor Management
   * permissions at all. So without Edit Team the list is the signed-in provider alone, which is
   * exactly the set of entries the save will be allowed to write.
   *
   * Fails closed: before the profile resolves `providerId` is undefined and nothing matches,
   * rather than briefly offering the whole facility.
   */
  const canSelectAnyTherapist = has(Permissions.EDIT_TEAM_LABOR_MANAGEMENT);
  const filteredOptions = allTherapist?.response?.data
    ?.filter((option: any) => canSelectAnyTherapist || option?.uuid === providerId)
    .sort((a: any, b: any) => a?.lastName?.localeCompare(b?.lastName));

  function calculateTimeDifference(clockIn: any, clockOut: any): string {
    const timeDifferenceInMillis: number =
      new Date(clockOut).getTime() - new Date(clockIn).getTime();
    const totalSeconds = Math.floor(timeDifferenceInMillis / 1000);
    const sign = totalSeconds < 0 ? '-' : '';
    const absTotalSeconds = Math.abs(totalSeconds);

    const hours = Math.floor(absTotalSeconds / 3600);
    const remainingSecondsAfterHours = absTotalSeconds % 3600;
    const minutes = Math.floor(remainingSecondsAfterHours / 60);
    const seconds = remainingSecondsAfterHours % 60;

    const hoursString = String(hours ? hours : '0').padStart(2, '0');
    const minutesString = String(minutes ? minutes : '0').padStart(2, '0');
    const secondsString = String(seconds ? seconds : '0').padStart(2, '0');

    return `${sign}${hoursString}:${minutesString}:${secondsString}`;
  }

  const pickerMaxDate = getFutureDateLimit(formik.values.type, formik.values.laborCode);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box style={modalStyle}>
        <Box style={modalContainerStyle}>
          <Stack>
            <Box sx={{ padding: '24px 24px 24px 24px' }}>
              <Typography variant="h6">
                {openHoursType?.type === 'EDIT' ? 'Update Labor Hours' : 'Add New Labor Hours'}{' '}
              </Typography>
            </Box>
            <Stack
              spacing={3}
              sx={{ padding: '24px 24px 24px 24px' }}
            >
              <Autocomplete
                disablePortal
                onBlur={formik.handleBlur}
                fullWidth
                getOptionLabel={(option: any) => {
                  return option?.lastName && option?.firstName
                    ? `${option.lastName} ${option.firstName}`
                    : '';
                }}
                onChange={(_event, newValue: any) => {
                  formik.setFieldValue('therapist', newValue?.uuid);
                }}
                value={
                  filteredOptions?.find((option: any) => option.uuid === formik.values.therapist) ||
                  null
                }
                id="combo-box-demo"
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?.uuid === value?.uuid;
                }}
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      key={option?.uuid}
                      value={option?.uuid}
                      {...props}
                    >
                      {`${option?.lastName} ${option?.firstName}`}
                    </MenuItem>
                  );
                }}
                options={filteredOptions || []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Therapist Name"
                    error={!!(formik.touched.therapist && formik.errors.therapist)}
                    helperText={formik.touched.therapist && (formik.errors.therapist as any)}
                  />
                )}
                loading={allTherapist?.loading}
              />
              <Grid
                xs={12}
                display={'flex'}
                gap={2}
                justifyContent={'space-between'}
              >
                <TextField
                  error={!!(formik.touched.type && formik.errors.type)}
                  fullWidth
                  helperText={formik.touched.type && (formik.errors.type as any)}
                  label="Location Type"
                  name="type"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.type}
                  select
                >
                  {locationType?.loading && (
                    <MenuItem
                      sx={{
                        cursor: 'none',
                        pointerEvents: 'none',
                      }}
                    >
                      location Type loading...
                    </MenuItem>
                  )}
                  {!!locationType?.response?.length &&
                    !locationType?.loading &&
                    locationType?.response?.map((option: any) => (
                      <MenuItem
                        key={option?.key}
                        value={option?.key}
                        sx={{ gap: 1, display: 'flex', alignItems: 'center' }}
                      >
                        <LaborLocationTypeIcons
                          style={{
                            marginRight: '10px',
                            position: 'relative',
                            top: '4px',
                          }}
                          iconType={option?.key}
                        />
                        {option?.displayName}
                      </MenuItem>
                    ))}
                </TextField>
                {formik.values?.type === 'BUILDING' && isLaborCodeDropdownShow && (
                  <Autocomplete
                    disablePortal
                    fullWidth
                    onBlur={formik.handleBlur}
                    getOptionLabel={(option: any) => option?.displayName || ''}
                    onChange={(_event, newValue: any) => {
                      formik.setFieldValue('laborCode', newValue);
                    }}
                    value={formik.values.laborCode}
                    id="combo-box-demo"
                    renderOption={(props, option) => {
                      return (
                        <MenuItem
                          key={option?.key}
                          value={option?.key}
                          {...props}
                        >
                          {option?.displayName}
                        </MenuItem>
                      );
                    }}
                    options={getLaborCodesResponseWithPermission || []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Labor Code"
                      />
                    )}
                  />
                )}
              </Grid>

              {formik.values.type === 'BUILDING' || formik.values.type === 'DRIVE' ? (
                <>
                  <DateTimePicker
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                    onChange={(e) => {
                      formik.setFieldValue('clockIn', e);
                    }}
                    label="Clock In"
                    value={formik.values.clockIn}
                    maxDate={pickerMaxDate}
                    slotProps={{
                      textField: {
                        name: 'clockIn',
                        error: !!formik.errors.clockIn,
                        // @ts-ignore
                        helperText: formik.errors.clockIn,
                      },
                    }}
                    // disableFuture
                    views={['hours', 'minutes']}
                  />

                  <DateTimePicker
                    label="Clock Out"
                    onChange={(e) => {
                      formik.setFieldValue('clockOut', e);
                    }}
                    value={formik.values.clockOut}
                    maxDate={pickerMaxDate}
                    // disableFuture
                    views={['hours', 'minutes']}
                    slotProps={{
                      textField: {
                        name: 'clockOut',
                        error: !!formik.errors.clockOut,
                        // @ts-ignore
                        helperText: formik.errors.clockOut,
                      },
                    }}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                  />

                  <TextField
                    label="Total Hours"
                    value={
                      calculateTimeDifference(formik.values.clockIn, formik.values.clockOut)
                        ? calculateTimeDifference(formik.values.clockIn, formik.values.clockOut)
                        : 0
                    }
                    fullWidth
                    disabled
                  />
                </>
              ) : null}

              {formik.values.type === 'MILEAGE' ? (
                <TextField
                  label="Mileage"
                  name="mileage"
                  error={!!(formik.touched.mileage && formik.errors.mileage)}
                  fullWidth
                  helperText={formik.touched.mileage && (formik.errors.mileage as any)}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.mileage}
                />
              ) : null}
              {has(Permissions.UPDATE_LABOR_MANAGEMENT) && checkActivityType ? (
                <>
                  {formik.values?.type === 'BUILDING' ? (
                    <FormControl sx={{ paddingLeft: '10px' }}>
                      <FormLabel id="demo-row-radio-buttons-group-label">Activity Type</FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        onChange={formik.handleChange}
                        value={formik.values.clockInActivityType}
                        name="clockInActivityType"
                      >
                        <FormControlLabel
                          value="THERAPY"
                          control={<Radio />}
                          label="Therapy"
                        />
                        <FormControlLabel
                          value="ADMINISTRATIVE"
                          control={<Radio />}
                          label="Administrative"
                        />
                      </RadioGroup>
                    </FormControl>
                  ) : null}
                </>
              ) : null}
            </Stack>
          </Stack>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              marginRight: '24px',
            }}
          >
            <Box sx={{ flexGrow: 1 }} />
            <Button
              color="inherit"
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              sx={{ ml: 1 }}
              type="submit"
              variant="contained"
              disabled={addLaborHrsResponse?.loading || editLaborHrsResponse?.loading}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
};

export default EditLaborManagement;
